import type { UndefinedOnPartialDeep } from 'type-fest';

import type { PendingAssetSubmission } from '@/store/asset-edit.store';
import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { fetchAlignProposal } from '@repo/hooks';
import type { OrderAsset } from '@repo/types/order';
import type { OrderUpdateCommand } from '@repo/types/order-update';
import type { Service } from '@repo/types/service';
import type { TimeWindowResponseItem } from '@repo/types/time-windows';
import { invariant } from '@repo/utils/type.utils';
import type { QueryClient } from '@tanstack/react-query';
import { buildModifyDeliveryTimeWindowCommand } from '@/commands/modify-delivery-time-window.utils';
import { buildModifyProvidedServiceTimeWindowCommand } from '@/commands/modify-provided-service-time-window.utils';
import { getOrderUpdatePendingSubmissionId, isOrderUpdateCommandNoop } from '@/commands/order-update.utils';

import { getServiceEndpoint } from '@/api/helpers.api';
import { getAppConfig } from '@/utils/window.utils';

import {
    buildTimeWindowAlignProposalRequest,
    evaluateTimeWindowAlignProposal,
} from './time-window-align-proposal.utils';

interface BuildPendingTimeWindowSubmissionsParams {
    baselineOrderData: OrderAsset;
    baselineServices: SelectedServices;
    orderData: OrderAsset;
    queryClient: QueryClient;
    selectedTimeWindow: TimeWindowResponseItem;
    service: UndefinedOnPartialDeep<Service>;
    serviceId: string;
    services: SelectedServices;
    isDeliveryTrigger: boolean;
    reasonCode: string;
}

/**
 * Builds pending submissions for the selected time window and any dependent service changes.
 */
export const buildPendingTimeWindowSubmissions = async ({
    baselineOrderData,
    baselineServices,
    orderData,
    queryClient,
    selectedTimeWindow,
    service,
    serviceId,
    services,
    isDeliveryTrigger,
    reasonCode,
}: BuildPendingTimeWindowSubmissionsParams): Promise<Map<string, PendingAssetSubmission | undefined>> => {
    const submissionUpdatesById = new Map<string, PendingAssetSubmission | undefined>();

    /**
     * Adds, replaces, or removes a pending submission based on whether the command changes the baseline order.
     */
    const addCommandToChangeSet = (command: OrderUpdateCommand) => {
        const id = getOrderUpdatePendingSubmissionId(command);
        const isNoop = isOrderUpdateCommandNoop({ orderData: baselineOrderData, services: baselineServices, command });
        const pendingSubmission: PendingAssetSubmission | undefined = isNoop
            ? undefined
            : {
                  id,
                  submission: {
                      type: 'orderUpdate',
                      payload: command,
                  },
              };

        submissionUpdatesById.set(id, pendingSubmission);
    };

    const primaryCommand = isDeliveryTrigger
        ? buildModifyDeliveryTimeWindowCommand({
              orderData: baselineOrderData,
              serviceId,
              timeWindow: selectedTimeWindow,
              reasonCode,
              ...(service.deliveryNo && { deliveryNo: service.deliveryNo }),
          })
        : buildModifyProvidedServiceTimeWindowCommand({
              orderData: baselineOrderData,
              serviceId,
              timeWindow: selectedTimeWindow,
              reasonCode,
          });

    addCommandToChangeSet(primaryCommand);

    const alignProposalRequest = buildTimeWindowAlignProposalRequest({
        orderData,
        services,
        serviceId,
        isDeliveryTrigger,
        timeWindow: selectedTimeWindow,
    });

    if (!alignProposalRequest.payload) {
        return submissionUpdatesById;
    }

    invariant(alignProposalRequest.queryParams, 'Cannot update time window: align proposal query params are missing');

    const alignProposal = await fetchAlignProposal(
        queryClient,
        getServiceEndpoint(getAppConfig().api.services.classicSalja, 'services'),
        alignProposalRequest.payload,
        alignProposalRequest.queryParams
    );

    const alignProposalEvaluation = evaluateTimeWindowAlignProposal({
        orderData,
        services,
        alignProposal,
        requestedServiceLines: alignProposalRequest.payload.serviceLines,
    });

    invariant(
        !alignProposalEvaluation.isBlocking,
        alignProposalEvaluation.errorCode ?? 'Cannot build dependent commands from a blocking align proposal result'
    );

    for (const affectedService of alignProposalEvaluation.affectedServices) {
        addCommandToChangeSet(
            buildModifyProvidedServiceTimeWindowCommand({
                orderData: baselineOrderData,
                serviceId: affectedService.serviceId,
                timeWindow: {
                    timeWindowId: affectedService.proposedTimeWindow.id,
                    fromDateTimeUtc: affectedService.proposedTimeWindow.fromDateTime,
                    toDateTimeUtc: affectedService.proposedTimeWindow.toDateTime,
                    timeZone: affectedService.proposedTimeWindow.timeZone,
                    capacityAllocated: affectedService.proposedTimeWindow.capacityAllocated,
                    paymentCutOffDateTimeUtc: undefined,
                },
                reasonCode,
            })
        );
    }

    return submissionUpdatesById;
};

/**
 * Merges time-window submission changes while preserving the order of existing pending submissions.
 */
export const mergePendingTimeWindowSubmissions = ({
    pendingSubmissions,
    submissionUpdatesById,
}: {
    pendingSubmissions: PendingAssetSubmission[];
    submissionUpdatesById: Map<string, PendingAssetSubmission | undefined>;
}): PendingAssetSubmission[] => {
    const remainingUpdatesById = new Map(submissionUpdatesById);
    const updatedPendingSubmissions = pendingSubmissions.flatMap((pendingSubmission) => {
        if (!remainingUpdatesById.has(pendingSubmission.id)) {
            return [pendingSubmission];
        }

        const updatedSubmission = remainingUpdatesById.get(pendingSubmission.id);
        remainingUpdatesById.delete(pendingSubmission.id);

        return updatedSubmission ? [updatedSubmission] : [];
    });
    const newPendingSubmissions = Array.from(remainingUpdatesById.values()).flatMap((pendingSubmission) =>
        pendingSubmission ? [pendingSubmission] : []
    );

    return [...updatedPendingSubmissions, ...newPendingSubmissions];
};
